import { createPortal } from 'react-dom';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_DOWN_COMMAND,
  TextNode,
} from 'lexical';
import getWebchatUrl from '@/utils/getWebchatUrl';
import getTeamsUrl from '@/utils/getTeamsUrl';
import type { MentionUser, NoteMention } from './types';

// Segmented text permits normal cursor navigation. An edit inside a mention turns it into plain text.
class MentionNode extends TextNode {
  __user: string;
  __name: string;
  static getType() {
    return 'note-mention';
  }
  static clone(node: MentionNode) {
    return new MentionNode(node.__user, node.__name, node.__key);
  }
  constructor(user: string, name: string, key?: string) {
    super(`@${name}`, key);
    this.__user = user;
    this.__name = name;
  }
  createDOM(config: any) {
    const element = super.createDOM(config);
    element.className = config.theme.mention || 'font-bold text-blue-700 dark:text-blue-300';
    return element;
  }
  exportJSON(): any {
    return {
      ...super.exportJSON(),
      type: 'note-mention',
      user: this.__user,
      name: this.__name,
      version: 1,
    };
  }
  static importJSON(data: any) {
    return new MentionNode(data.user, data.name);
  }
  isTextEntity() {
    return true;
  }
}
interface Props {
  value: string;
  mentions?: NoteMention[];
  onChange: (text: string, mentions: NoteMention[]) => void;
  onSubmit?: () => void;
  _customer?: string;
  _teamConversation?: string;
  _business?: string;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  embedded?: boolean;
  pinCaretToEnd?: boolean;
  /** Return saves (the default). Phones pass false: Return is a new line, the button saves. */
  submitOnEnter?: boolean;
  /** The phone composer's pill: one line that grows, no inner padding. */
  compact?: boolean;
  /** Extra classes for the editable area, e.g. a lower max height on short screens. */
  editorClassName?: string;
}
function serialize() {
  let text = '';
  const mentions: NoteMention[] = [];
  $getRoot()
    .getChildren()
    .forEach((paragraph, index) => {
      if (index) text += '\n';
      const children = 'getChildren' in paragraph ? (paragraph as any).getChildren() : [paragraph];
      for (const child of children) {
        const start = text.length;
        text += child.getTextContent();
        if (child instanceof MentionNode && child.getTextContent() === `@${child.__name}`)
          mentions.push({
            ...(child.__user === 'everyone'
              ? { kind: 'everyone' as const }
              : { _user: child.__user }),
            name: child.__name,
            start,
            end: text.length,
          });
      }
    });
  return { text, mentions };
}
function populate(value: string, mentions: NoteMention[]) {
  const root = $getRoot();
  root.clear();
  let offset = 0;
  for (const line of value.split('\n')) {
    const paragraph = $createParagraphNode();
    let cursor = offset;
    for (const mention of mentions.filter(
      (entry) => entry.start >= offset && entry.end <= offset + line.length,
    )) {
      if (mention.start < cursor || value.slice(mention.start, mention.end) !== `@${mention.name}`)
        continue;
      if (mention.start > cursor)
        paragraph.append($createTextNode(value.slice(cursor, mention.start)));
      paragraph.append(
        new MentionNode(
          mention.kind === 'everyone' ? 'everyone' : mention._user || '',
          mention.name,
        ).setMode('segmented'),
      );
      cursor = mention.end;
    }
    if (cursor < offset + line.length)
      paragraph.append($createTextNode(value.slice(cursor, offset + line.length)));
    root.append(paragraph);
    offset += line.length + 1;
  }
}
function Behaviour(props: Props) {
  const popupId = useId();
  const [editor] = useLexicalComposerContext();
  const latest = useRef(props);
  latest.current = props;
  const lastValue = useRef<string | null>(null);
  const lastSnapshot = useRef<string | null>(null);
  const [, reposition] = useState(0);
  const [query, setQuery] = useState<string | null>(null);
  const [users, setUsers] = useState<MentionUser[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const dismissed = useRef(false);
  const queryRef = useRef<string | null>(null);
  const matches = useMemo(
    () =>
      users.filter((user) =>
        `${user.name} ${user.email}`
          .toLocaleLowerCase()
          .includes((query || '').toLocaleLowerCase()),
      ),
    [users, query],
  );
  const matchesRef = useRef(matches);
  matchesRef.current = matches;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  useEffect(() => {
    editor.setEditable(!props.disabled);
  }, [editor, props.disabled]);
  useEffect(() => {
    const snapshot = JSON.stringify({ text: props.value, mentions: props.mentions || [] });
    if (lastSnapshot.current === snapshot) return;
    lastSnapshot.current = snapshot;
    lastValue.current = props.value;
    const pinCaretToEnd = Boolean(props.pinCaretToEnd);
    editor.update(() => {
      populate(props.value, props.mentions || []);
      if (pinCaretToEnd) $getRoot().selectEnd();
    }, { tag: 'external-note' });
    if (pinCaretToEnd) editor.blur();
  }, [editor, props.value, props.mentions, props.pinCaretToEnd]);
  useEffect(() => {
    if (props.autoFocus) editor.focus();
    return editor.registerUpdateListener(({ editorState, tags }) => {
      editorState.read(() => {
        const draft = serialize();
        if (!tags.has('external-note') && lastSnapshot.current !== JSON.stringify(draft)) {
          lastSnapshot.current = JSON.stringify(draft);
          dismissed.current = false;
          lastValue.current = draft.text;
          latest.current.onChange(draft.text, draft.mentions);
        }
        const selection = $getSelection();
        let keyword: string | null = null;
        if (!editor.isComposing() && $isRangeSelection(selection) && selection.isCollapsed()) {
          const node = selection.anchor.getNode();
          if ($isTextNode(node) && !(node instanceof MentionNode)) {
            const before = node.getTextContent().slice(0, selection.anchor.offset);
            const match = before.match(/(?:^|\s)@([^@\n]*)$/u);
            if (match) keyword = match[1];
          }
        }
        queryRef.current = dismissed.current ? null : keyword;
        setQuery(queryRef.current);
      });
    });
  }, [editor, props.autoFocus]);
  const fetchUsers = useCallback(() => {
    const controller = new AbortController();
    setLoading(true);
    setUsers([]);
    setError('');
    axios
      .get(
        props._teamConversation
          ? `${getTeamsUrl(window.location.href)}/api/team/mention-users`
          : `${getWebchatUrl(window.location.href)}/api/note-mentions/users`,
        {
          params: {
            _customer: props._customer,
            _business: props._business,
            _teamConversation: props._teamConversation,
          },
          signal: controller.signal,
          withCredentials: true,
        },
      )
      .then(({ data }) =>
        setUsers([
          {
            _id: 'everyone',
            name: 'everyone',
            email: props._teamConversation
              ? 'Everyone in this group'
              : 'Everyone with access to this customer',
          },
          ...(data.users || []),
        ]),
      )
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(err.response?.data?.message || 'Could not load teammates.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [props._customer, props._business, props._teamConversation]);
  const pickerOpen = query !== null;
  useEffect(() => {
    if (pickerOpen) return fetchUsers();
  }, [pickerOpen, fetchUsers]);
  useEffect(() => setSelected(query === null ? -1 : 0), [query]);
  const choose = (user: MentionUser) => {
    const keyword = queryRef.current;
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) || keyword === null) return;
      const node = selection.anchor.getNode();
      if (!$isTextNode(node)) return;
      const end = selection.anchor.offset;
      selection.setTextNodeRange(node, end - keyword.length - 1, node, end);
      selection.insertNodes([
        new MentionNode(user._id, user.name).setMode('segmented'),
        $createTextNode(' '),
      ]);
      const draft = serialize();
      lastValue.current = draft.text;
      lastSnapshot.current = JSON.stringify(draft);
      latest.current.onChange(draft.text, draft.mentions);
    });
    queryRef.current = null;
    setQuery(null);
    editor.focus();
  };
  const chooseRef = useRef(choose);
  chooseRef.current = choose;
  useEffect(
    () =>
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => {
          if (event.isComposing || editor.isComposing()) return false;
          if (queryRef.current !== null) {
            if (event.key === 'Escape') {
              event.preventDefault();
              dismissed.current = true;
              queryRef.current = null;
              setQuery(null);
              return true;
            }
            if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
              event.preventDefault();
              setSelected(
                (index) =>
                  (index +
                    (event.key === 'ArrowDown' ? 1 : -1) +
                    Math.max(1, matchesRef.current.length)) %
                  Math.max(1, matchesRef.current.length),
              );
              return true;
            }
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              const user = matchesRef.current[selectedRef.current];
              if (user) chooseRef.current(user);
              return true;
            }
          }
          if (
            event.key === 'Enter' &&
            !event.shiftKey &&
            latest.current.onSubmit &&
            latest.current.submitOnEnter !== false
          ) {
            event.preventDefault();
            latest.current.onSubmit();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor],
  );
  useEffect(() => {
    if (query === null) return;
    const update = () => reposition((n) => n + 1);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [query]);
  useEffect(() => {
    const element = editor.getRootElement();
    if (!element) return;
    element.setAttribute('aria-autocomplete', 'list');
    element.setAttribute('aria-expanded', String(query !== null && !props.disabled));
    element.setAttribute('aria-controls', `${popupId}-list`);
    if (query !== null && matches[selected]) {
      element.setAttribute('aria-activedescendant', `${popupId}-${selected}`);
      document.getElementById(`${popupId}-${selected}`)?.scrollIntoView({ block: 'nearest' });
    } else element.removeAttribute('aria-activedescendant');
  }, [editor, query, selected, matches, popupId, props.disabled]);
  if (query === null || props.disabled) return null;
  const root = editor.getRootElement();
  const bounds = root?.getBoundingClientRect();
  if (!bounds) return null;
  const container = (root.closest('[role="dialog"]') as HTMLElement) || document.body;
  const box =
    container === document.body
      ? { left: 0, top: 0, bottom: window.innerHeight }
      : container.getBoundingClientRect();
  const above = bounds.top - box.top - 8;
  const below = box.bottom - bounds.bottom - 8;
  const opensAbove = above >= Math.min(240, below);
  const scrollTop = container === document.body ? 0 : container.scrollTop;
  const style: any = {
    position: container === document.body ? 'fixed' : 'absolute',
    left: bounds.left - box.left,
    width: bounds.width,
    zIndex: 100,
    pointerEvents: 'auto',
    maxHeight: Math.min(240, Math.max(60, opensAbove ? above : below)),
    top: (opensAbove ? bounds.top - 4 : bounds.bottom + 4) - box.top + scrollTop,
    transform: opensAbove ? 'translateY(-100%)' : undefined,
  };
  return createPortal(
    <div
      style={style}
      className="overflow-y-auto rounded-lg border bg-popover p-1 shadow-lg"
      id={`${popupId}-list`}
      role="listbox"
      aria-label="Tag a teammate"
    >
      {loading ? (
        <p className="p-3 text-sm text-muted-foreground">Loading teammates…</p>
      ) : error ? (
        <button type="button" className="p-3 text-sm" onClick={fetchUsers}>
          {error} Retry
        </button>
      ) : matches.length === 0 ? (
        <p className="p-3 text-sm text-muted-foreground">No teammates found</p>
      ) : (
        matches.map((user, index) => (
          <button
            type="button"
            id={`${popupId}-${index}`}
            role="option"
            aria-selected={index === selected}
            key={user._id}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => choose(user)}
            className={`flex w-full items-center gap-3 rounded-md p-2 text-left ${index === selected ? 'bg-accent' : ''}`}
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
              {user.name
                .split(' ')
                .map((part) => part[0])
                .slice(0, 2)
                .join('')}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{user.name}</span>
              <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
            </span>
          </button>
        ))
      )}
    </div>,
    container,
  );
}
export function NoteEditor(props: Props) {
  return (
    <LexicalComposer
      initialConfig={{
        namespace: 'customer-note',
        nodes: [MentionNode],
        onError: (error) => console.error('Note editor error', error),
        theme: {
          paragraph: 'm-0',
          mention: props._teamConversation
            ? 'font-bold text-violet-700 dark:text-violet-300'
            : 'font-bold text-blue-700 dark:text-blue-300',
        },
      }}
    >
      <div
        className={`relative min-w-0 ${props.embedded ? 'bg-transparent' : 'rounded-md border bg-background'}`}
      >
        <Behaviour {...props} />
        <PlainTextPlugin
          contentEditable={
            <ContentEditable
              aria-label={props._teamConversation ? 'Group message' : 'Note'}
              className={`${
                props.compact
                  ? 'max-h-[min(9rem,calc(var(--app-height)*0.3))] min-h-[42px] overflow-y-auto whitespace-pre-wrap break-words py-[9px] text-base leading-6 outline-none'
                  : 'max-h-80 min-h-20 overflow-y-auto whitespace-pre-wrap break-words p-3 text-sm outline-none max-md:text-base'
              }${props.editorClassName ? ` ${props.editorClassName}` : ''}`}
            />
          }
          placeholder={
            <div
              className={
                props.compact
                  ? 'pointer-events-none absolute left-0 top-[9px] truncate text-base leading-6 text-muted-foreground'
                  : 'pointer-events-none absolute left-3 top-3 text-sm text-muted-foreground max-md:text-base'
              }
            >
              {props.placeholder ||
                (props._teamConversation
                  ? 'Write a message… Type @ to tag a teammate'
                  : 'Write a note… Type @ to tag a teammate')}
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        {props.value.length > 10000 && (
          <p role="alert" className="px-3 pb-2 text-sm text-destructive">
            Notes can be up to 10,000 characters. Please shorten this note.
          </p>
        )}
      </div>
    </LexicalComposer>
  );
}
